<template>
  <div class="wrapper-form">
    <BasicTabs
      v-if="!isMobile"
      v-model="modelValue"
      :tabs
      :tabsPlacement
    />
    <BasicTab
      v-else
      :tabKey="currentTab?.tabKey"
      :tabState="currentTab?.tabState"
      class="wrapper-form__tabs--mobile"
    />
    <div class="wrapper-form__main">
      <div
        v-if="isMobile || showStepper"
        class="wrapper-form__header"
      >
        <BasicText class="wrapper-form__title">
          {{
            (currentTab?.tabKey === 'Synthèse' && addTabInfo
              ? currentTab?.tabKey + ' : ' + addTabInfo
              : currentTab?.tabKey) || ''
          }}
        </BasicText>
        <div class="wrapper-form__stepper">
          <BasicButton
            :disabled="!canMovePrevious"
            label="Précédent"
            type="secondary"
            variant="ghost"
            size="small"
            iconName="arrow-left"
            @click="handleMovePrevious"
          />
          <BasicButton
            :disabled="!canMoveNext"
            label="Suivant"
            type="secondary"
            variant="ghost"
            size="small"
            iconRight
            iconName="arrow-right"
            @click="handleMoveNext"
          />
        </div>
      </div>
      <div
        ref="contentRef"
        class="wrapper-form__content scrollbar"
      >
        <slot />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { toRef, ref, watch, inject } from 'vue'
  import { useWrapperFormLogic, type TabsStepperEmit } from './useWrapperFormLogic'
  import type { TabsModel } from '@designSystem/components/basic/tabs/BasicTabs.types'
  import { DEVICE_BREAKPOINT } from '@/plugin/device-breakpoint'

  const modelValue = defineModel<TabsModel>()

  const props = withDefaults(
    defineProps<{
      tabs: TabProps[]
      showStepper?: boolean
      tabsPlacement?: 'center' | 'start'
      addTabInfo?: string
      disableParentScroll?: boolean
    }>(),
    { tabsPlacement: 'center', disableParentScroll: true },
  )

  const { isMobile } = inject(DEVICE_BREAKPOINT)!

  const emit = defineEmits<TabsStepperEmit>()

  const contentRef = ref<HTMLElement | null>(null)

  watch(modelValue, () => {
    requestAnimationFrame(() => contentRef.value?.scrollTo({ top: 0, behavior: 'smooth' }))
  })

  const { currentTab, canMovePrevious, canMoveNext, handleMovePrevious, handleMoveNext } = useWrapperFormLogic(
    modelValue,
    toRef(props, 'tabs'),
    emit,
  )
</script>

<style scoped lang="less">
  .wrapper-form {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    border-bottom-right-radius: 16px;
    border-bottom-left-radius: 16px;
    height: 100%;

    &__main {
      flex: 1;
      min-height: 0;
      padding: @spacing-15;
      background-color: white;
      display: flex;
      flex-direction: column;
      overflow: hidden;
    }

    &__header {
      padding: @spacing-15;
      display: flex;
      justify-content: space-between;
      align-items: center;
      flex: none;
      z-index: 10;
    }

    &__stepper {
      display: flex;
      justify-content: space-between;
      gap: @spacing-15;
    }

    &__title {
      color: @primary-600;
      font-weight: @font-weight-bold;
      font-size: @font-size-h4;
    }

    &__content {
      margin-right: @spacing-5;
      padding: 0 @spacing-15 @spacing-20 @spacing-15;
      display: flex;
      flex-direction: column;
      gap: 20px;
      flex: 1;
      min-height: 0;
      overflow-x: hidden;
      overflow-y: auto;
    }
  }

  @container (max-width: @breakpoint-mobile) {
    .wrapper-form {
      &__stepper {
        flex: 1;
      }
      &__content {
        padding: 0 12px;
        gap: 20px;
      }
      &__title {
        display: none;
      }
      &__tabs--mobile {
        max-width: 300px;
        width: 300px;
        height: 60px;
        align-self: center;
        display: flex;
        justify-content: center;
        align-items: center;
        padding: @spacing-15 @spacing-20;
        gap: @spacing-5;
        user-select: none;
        font-size: @font-size-body-l;
        border-top-left-radius: @spacing-15;
        border-top-right-radius: @spacing-15;
        background-color: @white;
        :deep(.text) {
          font-weight: 900 !important;
        }
      }
    }
  }
  @container (max-width: 300px) {
    .wrapper-form__stepper :deep(.btn__label) {
      display: none;
    }
  }
</style>
